/**
 * What a visitor sees on an institute's own domain when master has switched
 * it off (Access control), or when the domain belongs to nobody.
 *
 *   variant "blocked"    the institute's name, logo and master's message.
 *                        `offerSignIn` adds a Sign in button when only the
 *                        public site is off and the portal is still open.
 *   variant "not-found"  no name, no logo, no platform branding at all: an
 *                        unknown domain must never show EduExpertAI pages.
 *
 * Always noindex, so search engines do not keep an "unavailable" page.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useBranding, useBrandName } from '@/contexts/BrandingContext';

interface Props {
  variant: 'blocked' | 'not-found';
  message?: string;
  offerSignIn?: boolean;
  /** Override the branding context (the public site on the path address knows its institute itself). */
  brandName?: string;
  logoUrl?: string;
  /** Where "Sign in" goes (default /login). */
  signInHref?: string;
}

const NOT_FOUND_MESSAGE =
  'This site is not available. If you expected to find your institute here, please contact your institute administrator.';

const SiteUnavailable: React.FC<Props> = ({
  variant, message, offerSignIn = false, brandName: nameOverride, logoUrl: logoOverride, signInHref = '/login',
}) => {
  const contextName = useBrandName();
  const brandName = nameOverride || contextName;
  const { branding } = useBranding();
  const branded = variant === 'blocked';
  // The institute's own mark only. useBrandLogo would fall back to the
  // platform's, which must never appear on a customer's domain.
  const [logoFailed, setLogoFailed] = React.useState(false);
  const sourceLogo = logoOverride ?? branding.logoUrl;
  const logoUrl = sourceLogo && !logoFailed ? sourceLogo : '';

  const title = branded ? brandName : 'Site not available';
  const text = branded
    ? message || 'This institute is temporarily unavailable. Please contact your institute administrator.'
    : NOT_FOUND_MESSAGE;

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center px-4 py-12">
      <Helmet>
        <title>{title}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Card className="w-full max-w-md border border-border/50 shadow-sm">
        <CardContent className="flex flex-col items-center text-center gap-5 p-8">
          {branded && logoUrl && (
            <img
              src={logoUrl}
              alt=""
              className="h-20 w-20 rounded-xl object-contain"
              onError={() => setLogoFailed(true)}
            />
          )}
          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-foreground">{title}</h1>
            <p className="text-muted-foreground leading-relaxed">{text}</p>
          </div>
          {branded && offerSignIn && (
            <Button asChild className="rounded-xl">
              <Link to={signInHref}>Sign in</Link>
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SiteUnavailable;
